import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/common/PageHeader';
import { apiService } from '../services/apiService';
import {
  listAdminPromoCodes,
  updateAdminPromoCode,
  type PromoCode,
} from '../services/paymentsService';

interface PlatformStats {
  totalUsers: number;
  totalVCs: number;
  totalAdmins: number;
}

interface VCInviteDetails {
  inviteId: string;
  email: string;
  invitedAt: string;
  expiresAt: string;
  inviteStatus: 'pending' | 'accepted' | 'expired';
  acceptedAt: string | null;
  userId: string | null;
  fullName: string | null;
  onboardingStatus: 'completed' | 'in_progress' | 'not_started';
  onboardingData: {
    completed: boolean;
    currentStep?: number;
    completedAt?: string;
    fundName?: string;
    fundWebsite?: string;
  } | null;
  portfolioCompanyCount: number;
}

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<PlatformStats>({
    totalUsers: 0,
    totalVCs: 0,
    totalAdmins: 0,
  });
  const [vcInvites, setVcInvites] = useState<VCInviteDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<'invitedAt' | 'inviteStatus' | 'onboardingStatus'>('invitedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Promo code state
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [promoActionLoading, setPromoActionLoading] = useState<string | null>(null);
  const [copiedPromoId, setCopiedPromoId] = useState<string | null>(null);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      // Fetch stats, VC invites, and promo codes in parallel
      const [statsResponse, invitesResponse, promoResponse] = await Promise.all([
        apiService.getAdminStats(),
        apiService.getVCInviteDetails(),
        listAdminPromoCodes().catch(() => ({ success: false, data: [] as PromoCode[] })),
      ]);

      if (statsResponse.success) {
        setStats(statsResponse.data);
      }

      if (invitesResponse.success) {
        setVcInvites(invitesResponse.data);
      }

      if (promoResponse.success) {
        setPromoCodes(promoResponse.data);
      }
    } catch (err) {
      console.error('Error fetching admin data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInvite = async (inviteId: string, email: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the invite for ${email}? This action cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    try {
      setActionLoading(inviteId);
      setError(null);
      setSuccessMessage(null);

      const response = await apiService.deleteVCInvite(inviteId);

      if (response.success) {
        setSuccessMessage('Invite deleted successfully');
        // Remove the invite from local state
        setVcInvites(vcInvites.filter(invite => invite.inviteId !== inviteId));
      }
    } catch (err) {
      console.error('Error deleting invite:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete invite');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResendInvite = async (inviteId: string, email: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to resend the invite to ${email}? A new invitation email will be sent.`
    );

    if (!confirmed) {
      return;
    }

    try {
      setActionLoading(inviteId);
      setError(null);
      setSuccessMessage(null);

      const response = await apiService.resendVCInvite(inviteId);

      if (response.success) {
        setSuccessMessage(`Invite resent successfully to ${email}`);
        // Refresh the data to get updated expiration date
        await fetchAdminData();
      }
    } catch (err) {
      console.error('Error resending invite:', err);
      setError(err instanceof Error ? err.message : 'Failed to resend invite');
    } finally {
      setActionLoading(null);
    }
  };

  const handleTogglePromo = async (id: string, activate: boolean) => {
    setPromoActionLoading(id);
    try {
      const result = await updateAdminPromoCode(id, { is_active: activate });
      if (result.success) {
        setSuccessMessage(`Promo code ${activate ? 'activated' : 'deactivated'} successfully`);
        setPromoCodes(promoCodes.map(p => p.id === id ? { ...p, is_active: activate } : p));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update promo code');
    } finally {
      setPromoActionLoading(null);
    }
  };

  const handleCopyPromoLink = async (promo: PromoCode) => {
    const link = `${window.location.origin}/signup?promo=${encodeURIComponent(promo.code)}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedPromoId(promo.id);
      setTimeout(() => setCopiedPromoId(null), 2000);
    } catch {
      // Fallback for insecure contexts
      const textArea = document.createElement('textarea');
      textArea.value = link;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedPromoId(promo.id);
      setTimeout(() => setCopiedPromoId(null), 2000);
    }
  };

  const getPromoStatusBadge = (isActive: boolean) => {
    const badgeStyles = isActive
      ? 'bg-green-600/20 text-green-400 border-green-600/50'
      : 'bg-red-600/20 text-red-400 border-red-600/50';
    return (
      <span className={`px-2 py-1 rounded-md text-xs font-semibold border ${badgeStyles}`}>
        {isActive ? 'ACTIVE' : 'INACTIVE'}
      </span>
    );
  };

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedVcInvites = [...vcInvites].sort((a, b) => {
    let comparison = 0;
    
    if (sortField === 'invitedAt') {
      comparison = new Date(a.invitedAt).getTime() - new Date(b.invitedAt).getTime();
    } else if (sortField === 'inviteStatus') {
      comparison = a.inviteStatus.localeCompare(b.inviteStatus);
    } else if (sortField === 'onboardingStatus') {
      comparison = a.onboardingStatus.localeCompare(b.onboardingStatus);
    }

    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const getInviteStatusBadge = (status: string) => {
    const styles = {
      accepted: 'bg-green-600/20 text-green-400 border-green-600/50',
      pending: 'bg-orange-600/20 text-orange-400 border-orange-600/50',
      expired: 'bg-red-600/20 text-red-400 border-red-600/50',
    };
    return (
      <span className={`px-2 py-1 rounded-md text-xs font-semibold border ${styles[status as keyof typeof styles]}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  const getOnboardingStatusBadge = (status: string) => {
    const styles = {
      completed: 'bg-green-600/20 text-green-400 border-green-600/50',
      in_progress: 'bg-blue-600/20 text-blue-400 border-blue-600/50',
      not_started: 'bg-gray-600/20 text-gray-400 border-gray-600/50',
    };
    const labels = {
      completed: 'COMPLETED',
      in_progress: 'IN PROGRESS',
      not_started: 'NOT STARTED',
    };
    return (
      <span className={`px-2 py-1 rounded-md text-xs font-semibold border ${styles[status as keyof typeof styles]}`}>
        {labels[status as keyof typeof labels]}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="max-w-7xl mx-auto">
      <PageHeader
        title="Platform Administration"
        subtitle={`Welcome, ${user?.email || 'Administrator'}`}
      />

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-gray-400">Loading platform statistics...</p>
        </div>
      ) : error ? (
        <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-6 mb-8">
          <div className="flex items-center">
            <svg className="w-6 h-6 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-300">{error}</p>
          </div>
        </div>
      ) : (
        <>
          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-900/20 border border-green-800/50 rounded-lg p-6 mb-8">
              <div className="flex items-center">
                <svg className="w-6 h-6 text-green-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-green-300">{successMessage}</p>
              </div>
            </div>
          )}
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-lg p-6 hover:border-purple-500 transition-all hover:shadow-lg hover:shadow-purple-500/20">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center mr-4">
                  <span className="text-2xl">👤</span>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">{stats.totalUsers}</div>
                  <div className="text-sm text-gray-400">Regular Users</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-lg p-6 hover:border-purple-500 transition-all hover:shadow-lg hover:shadow-purple-500/20">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-green-600/20 rounded-lg flex items-center justify-center mr-4">
                  <span className="text-2xl">🏢</span>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">{stats.totalVCs}</div>
                  <div className="text-sm text-gray-400">VC Firms</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-lg p-6 hover:border-purple-500 transition-all hover:shadow-lg hover:shadow-purple-500/20">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-purple-600/20 rounded-lg flex items-center justify-center mr-4">
                  <span className="text-2xl">⚙️</span>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">{stats.totalAdmins}</div>
                  <div className="text-sm text-gray-400">Platform Admins</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-lg p-6 hover:border-purple-500 transition-all hover:shadow-lg hover:shadow-purple-500/20">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-orange-600/20 rounded-lg flex items-center justify-center mr-4">
                  <span className="text-2xl">🎟️</span>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">{promoCodes.length}</div>
                  <div className="text-sm text-gray-400">Promo Codes</div>
                </div>
              </div>
            </div>
          </div>

          {/* Admin Tools Section (promoted to top of page for discoverability) */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-white mb-6">Admin Tools</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => navigate('/admin/newsletters')}
                className="text-left bg-gray-800/60 border border-gray-700 rounded-lg p-6 hover:border-purple-500 hover:bg-gray-800 transition-all"
              >
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-3">📰</span>
                  <h3 className="text-lg font-semibold text-white">Newsletters</h3>
                </div>
                <p className="text-sm text-gray-400">
                  Create, schedule, and broadcast newsletters to all users.
                </p>
              </button>
              <button
                onClick={() => navigate('/admin/blog')}
                className="text-left bg-gray-800/60 border border-gray-700 rounded-lg p-6 hover:border-purple-500 hover:bg-gray-800 transition-all"
              >
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-3">📝</span>
                  <h3 className="text-lg font-semibold text-white">Blog</h3>
                </div>
                <p className="text-sm text-gray-400">
                  Manage AI-generated blog articles, topics, and publishing.
                </p>
              </button>
              <button
                onClick={() => navigate('/admin/invite-vc')}
                className="text-left bg-gray-800/60 border border-gray-700 rounded-lg p-6 hover:border-purple-500 hover:bg-gray-800 transition-all"
              >
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-3">🏢</span>
                  <h3 className="text-lg font-semibold text-white">Invite VC</h3>
                </div>
                <p className="text-sm text-gray-400">
                  Send an invitation to a venture capital firm.
                </p>
              </button>
            </div>
          </div>

          {/* VC Invites Table */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-lg p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">VC Invites</h2>
              <button
                onClick={() => navigate('/admin/invite-vc')}
                className="px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Invite VC
              </button>
            </div>

            {vcInvites.length === 0 ? (
              <div className="text-center py-12">
                <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <p className="text-gray-400 text-lg mb-2">No VC invites yet</p>
                <p className="text-gray-500 text-sm">Click &ldquo;Invite VC&rdquo; to send your first invitation</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">VC Email</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">VC Firm Name</th>
                      <th 
                        className="text-left py-3 px-4 text-sm font-semibold text-gray-300 cursor-pointer hover:text-white"
                        onClick={() => handleSort('invitedAt')}
                      >
                        <div className="flex items-center">
                          Invited Date
                          {sortField === 'invitedAt' && (
                            <svg className={`w-4 h-4 ml-1 transition-transform ${sortDirection === 'asc' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          )}
                        </div>
                      </th>
                      <th 
                        className="text-left py-3 px-4 text-sm font-semibold text-gray-300 cursor-pointer hover:text-white"
                        onClick={() => handleSort('inviteStatus')}
                      >
                        <div className="flex items-center">
                          Invite Status
                          {sortField === 'inviteStatus' && (
                            <svg className={`w-4 h-4 ml-1 transition-transform ${sortDirection === 'asc' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          )}
                        </div>
                      </th>
                      <th 
                        className="text-left py-3 px-4 text-sm font-semibold text-gray-300 cursor-pointer hover:text-white"
                        onClick={() => handleSort('onboardingStatus')}
                      >
                        <div className="flex items-center">
                          Onboarding
                          {sortField === 'onboardingStatus' && (
                            <svg className={`w-4 h-4 ml-1 transition-transform ${sortDirection === 'asc' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          )}
                        </div>
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Portfolio Companies</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedVcInvites.map((invite) => (
                      <tr key={invite.inviteId} className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors">
                        <td className="py-4 px-4">
                          <div className="text-white text-sm">{invite.email}</div>
                          {invite.fullName && (
                            <div className="text-gray-400 text-xs mt-1">{invite.fullName}</div>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          {invite.onboardingData?.fundName ? (
                            <div>
                              <div className="text-white text-sm font-medium">{invite.onboardingData.fundName}</div>
                              {invite.onboardingData.fundWebsite && (
                                <a 
                                  href={invite.onboardingData.fundWebsite} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-purple-400 hover:text-purple-300 text-xs flex items-center mt-1"
                                >
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                  Website
                                </a>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-500 text-sm">—</span>
                          )}
                        </td>
                        <td className="py-4 px-4 text-gray-300 text-sm">
                          {formatDate(invite.invitedAt)}
                        </td>
                        <td className="py-4 px-4">
                          {getInviteStatusBadge(invite.inviteStatus)}
                        </td>
                        <td className="py-4 px-4">
                          {getOnboardingStatusBadge(invite.onboardingStatus)}
                        </td>
                        <td className="py-4 px-4">
                          {invite.onboardingStatus === 'completed' ? (
                            <div className="flex items-center">
                              <svg className="w-4 h-4 text-purple-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              <span className="text-white text-sm font-medium">{invite.portfolioCompanyCount}</span>
                            </div>
                          ) : (
                            <span className="text-gray-500 text-sm">—</span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-2">
                            {/* Resend button - only for pending or expired invites */}
                            {(invite.inviteStatus === 'pending' || invite.inviteStatus === 'expired') && (
                              <button
                                onClick={() => handleResendInvite(invite.inviteId, invite.email)}
                                disabled={actionLoading === invite.inviteId}
                                className="px-3 py-1.5 bg-blue-600/20 text-blue-400 border border-blue-600/50 rounded-md text-xs font-semibold hover:bg-blue-600/30 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                title="Resend invite email"
                              >
                                {actionLoading === invite.inviteId ? (
                                  <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <>
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    Resend
                                  </>
                                )}
                              </button>
                            )}
                            
                            {/* Remove button - only for non-accepted invites */}
                            {invite.inviteStatus !== 'accepted' && (
                              <button
                                onClick={() => handleDeleteInvite(invite.inviteId, invite.email)}
                                disabled={actionLoading === invite.inviteId}
                                className="px-3 py-1.5 bg-red-600/20 text-red-400 border border-red-600/50 rounded-md text-xs font-semibold hover:bg-red-600/30 hover:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                title="Remove invite"
                              >
                                {actionLoading === invite.inviteId ? (
                                  <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <>
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    Remove
                                  </>
                                )}
                              </button>
                            )}

                            {/* Show placeholder for accepted invites with no actions */}
                            {invite.inviteStatus === 'accepted' && (
                              <span className="text-gray-500 text-xs italic">No actions available</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Promo Codes Section */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-lg p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Promo Codes</h2>
              <button
                onClick={() => navigate('/admin/promo/create')}
                className="px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Promo Code
              </button>
            </div>

            {promoCodes.length === 0 ? (
              <div className="text-center py-12">
                <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <p className="text-gray-400 text-lg mb-2">No promo codes yet</p>
                <p className="text-gray-500 text-sm">Click &ldquo;Create Promo Code&rdquo; to create your first promotional code</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Code</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Plan</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Duration</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Redemptions</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Created</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promoCodes.map((promo) => (
                      <tr key={promo.id} className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors">
                        <td className="py-4 px-4">
                          <span className="font-mono text-white font-medium tracking-wider">{promo.code}</span>
                          {promo.description && (
                            <div className="text-gray-400 text-xs mt-1">{promo.description}</div>
                          )}
                        </td>
                        <td className="py-4 px-4 text-gray-300 text-sm">
                          {promo.plan_tier.toUpperCase()}
                        </td>
                        <td className="py-4 px-4 text-gray-300 text-sm">
                          {promo.duration_months} months
                        </td>
                        <td className="py-4 px-4 text-gray-300 text-sm">
                          {promo.current_redemptions}{promo.max_redemptions ? `/${promo.max_redemptions}` : ''}
                        </td>
                        <td className="py-4 px-4">
                          {getPromoStatusBadge(promo.is_active)}
                        </td>
                        <td className="py-4 px-4 text-gray-300 text-sm">
                          {new Date(promo.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleCopyPromoLink(promo)}
                              className="px-3 py-1.5 bg-purple-600/20 text-purple-400 border border-purple-600/50 rounded-md text-xs font-semibold hover:bg-purple-600/30 hover:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all flex items-center"
                              title={`Copy signup link for ${promo.code}`}
                            >
                              {copiedPromoId === promo.id ? (
                                <>
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  Copied!
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                  </svg>
                                  Copy Link
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => handleTogglePromo(promo.id, !promo.is_active)}
                              disabled={promoActionLoading === promo.id}
                              className={`px-3 py-1.5 rounded-md text-xs font-semibold border focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center ${
                                promo.is_active
                                  ? 'bg-red-600/20 text-red-400 border-red-600/50 hover:bg-red-600/30 hover:border-red-500 focus:ring-red-500'
                                  : 'bg-green-600/20 text-green-400 border-green-600/50 hover:bg-green-600/30 hover:border-green-500 focus:ring-green-500'
                              }`}
                            >
                              {promoActionLoading === promo.id ? (
                                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                              ) : (
                                promo.is_active ? 'Deactivate' : 'Activate'
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Coming Soon Section */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">More features</h2>
            </div>
            <p className="text-gray-400 mb-6">
              Additional platform administration features:
            </p>
            <ul className="space-y-3 mb-8">
              <li className="flex items-start text-gray-300">
                <svg className="w-6 h-6 text-purple-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                View platform-wide statistics
              </li>
              <li className="flex items-start text-gray-300">
                <svg className="w-6 h-6 text-purple-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Monitor VC invites and onboarding progress
              </li>
              <li className="flex items-start text-gray-300">
                <svg className="w-6 h-6 text-purple-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Track portfolio company relationships
              </li>
            </ul>
            <div className="bg-gradient-to-r from-purple-900/30 to-purple-800/20 border border-purple-700/50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <span className="bg-purple-600 text-white px-3 py-1 rounded-md text-xs font-semibold uppercase tracking-wider mr-3">
                  Coming Soon
                </span>
              </div>
              <p className="text-purple-200">
                User management, analytics, and more advanced admin features
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
